import React, { useState } from 'react';
import { SUPABASE_URL } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, FileText, Share2, Image, Loader2 } from 'lucide-react';
import { getLocationParts, getTimezone } from '@/utils/timeFormatting';
import { trackExportDownload } from '@/utils/analytics';
import { useToast } from '@/hooks/use-toast';
import { jsonToCSV } from '@/utils/excelExport';

interface LocationData {
  name: string;
  latitude: number;
  longitude: number;
}

interface DayData {
  date: number;
  fullDate: string;
  sunrise: string;
  sunset: string;
  moonrise: string;
  moonset: string;
  astroTwilightMorning: string;
  astroTwilightEvening: string;
  galacticCoreVisible: boolean;
  galacticCoreStart: string;
  galacticCoreEnd: string;
  moonPhase: number;
  notVisibleReason?: string;
  maxElevation?: number;
}

interface FreeUserHeaderProps {
  location: LocationData;
  month: number; // 0-indexed month (0 = January, 11 = December)
  year: number;
  monthData: DayData[];
}

export const FreeUserHeader: React.FC<FreeUserHeaderProps> = ({
  location,
  month,
  year,
  monthData,
}) => {
  const { toast } = useToast();
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const locationParts = getLocationParts(location.name);
  const timezone = getTimezone(location.latitude, location.longitude);

  const formatLocationName = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  };

  const handleShare = async () => {
    const baseUrl = 'https://www.milkywayplanner.com';
    const params = new URLSearchParams({
      location: location.name,
      lat: location.latitude.toString(),
      lon: location.longitude.toString(),
      month: month.toString(),
      year: year.toString()
    });
    const shareUrl = `${baseUrl}/?${params.toString()}`;
    
    // Try native share API on mobile, fallback to clipboard
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Milky Way Plan - ${locationParts.city}, ${monthNames[month]} ${year}`,
          text: `Check out the best nights for Milky Way photography in ${locationParts.city} for ${monthNames[month]} ${year}`,
          url: shareUrl
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fallback to clipboard
      }
    }
    
    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link copied!",
        description: "Share this plan with others.",
      });
    } catch (err) {
      toast({
        title: "Unable to copy",
        description: "Please copy the URL from your browser.",
        variant: "destructive"
      });
    }
  };

  const getMoonPhaseText = (moonPhase: number) => {
    const moonPercent = Math.round(moonPhase * 100);
    if (moonPercent <= 5) return 'New Moon';
    else if (moonPercent <= 25) return 'Waxing Crescent';
    else if (moonPercent <= 45) return 'First Quarter';
    else if (moonPercent <= 55) return 'Waxing Gibbous';
    else if (moonPercent <= 95) return 'Full Moon';
    else return 'Waning';
  };

  const getVisibilityRating = (moonPhase: number, galacticCoreStart: string) => {
    const moonPercent = Math.round(moonPhase * 100);
    
    if (galacticCoreStart === 'N/A' || 
        galacticCoreStart === '--:--' || 
        galacticCoreStart === 'Below horizon' ||
        galacticCoreStart === 'Not visible' ||
        galacticCoreStart === 'No twilight' ||
        galacticCoreStart === 'Not dark enough' ||
        galacticCoreStart === 'Too brief') return 0;
    
    if (moonPercent <= 5) return 10;
    else if (moonPercent <= 10) return 9;
    else if (moonPercent <= 15) return 8;
    else if (moonPercent <= 20) return 7;
    else if (moonPercent <= 25) return 6;
    else if (moonPercent <= 35) return 5;
    else if (moonPercent <= 45) return 4;
    else if (moonPercent <= 55) return 3;
    else if (moonPercent <= 70) return 2;
    else if (moonPercent <= 85) return 1;
    else return 0;
  };

  const exportToCSV = () => {
    const monthName = monthNames[month].toLowerCase();
    const locationFormatted = formatLocationName(location.name);
    const filename = `milky-way-planner-${locationFormatted}-${monthName}-${year}.csv`;

    trackExportDownload('csv', location.name);

    const csvData = monthData.map(day => ({
      'Date': day.fullDate,
      'Sunrise': day.sunrise,
      'Sunset': day.sunset,
      'Moonrise': day.moonrise,
      'Moonset': day.moonset,
      'Core Start': day.galacticCoreStart,
      'Core End': day.galacticCoreEnd,
      'Moon Percentage': `${Math.round(day.moonPhase * 100)}%`,
      'Phase Name': getMoonPhaseText(day.moonPhase),
      'Visibility Rating': getVisibilityRating(day.moonPhase, day.galacticCoreStart)
    }));

    const csv = jsonToCSV(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadImage = async () => {
    setIsDownloadingImage(true);
    try {
      const imageUrl = `${SUPABASE_URL}/functions/v1/generate-og-image?location=${encodeURIComponent(location.name)}&lat=${location.latitude}&lon=${location.longitude}&month=${month}&year=${year}`;
      
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error('Failed to generate image');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const monthName = monthNames[month].toLowerCase();
      const locationFormatted = formatLocationName(location.name);
      link.download = `milky-way-planner-${locationFormatted}-${monthName}-${year}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      trackExportDownload('image', location.name);
      
      toast({
        title: "Image downloaded!",
        description: "Share this image on social media or with friends.",
      });
    } catch (error) {
      console.error('Error downloading image:', error);
      toast({
        title: "Download failed",
        description: "Unable to generate the image. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDownloadingImage(false);
    }
  };

  return (
    <Card className="glass-card border-border/50 shadow-lg">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="flex items-center text-3xl font-light text-foreground">
              <MapPin className="w-7 h-7 mr-3 text-amber-600" />
              {monthNames[month]} {year} - {locationParts.city}
              {locationParts.stateProvince && `, ${locationParts.stateProvince}`}
            </CardTitle>
            <div className="space-y-2 mt-4">
              <div className="text-muted-foreground">
                Coordinates: {location.latitude.toFixed(4)}°, {location.longitude.toFixed(4)}°
              </div>
              <div className="flex items-center text-muted-foreground">
                <Clock className="w-4 h-4 mr-2" />
                Times in {timezone}
              </div>
            </div>
          </div>
          
          {/* Action Buttons for Free Users */}
          <div className="flex gap-2">
            <Button onClick={handleShare} variant="outline" size="sm">
              <Share2 className="mr-2 h-4 w-4" />
              Share
            </Button>
            <Button 
              onClick={handleDownloadImage} 
              variant="outline" 
              size="sm"
              disabled={isDownloadingImage}
            >
              {isDownloadingImage ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Image className="mr-2 h-4 w-4" />
              )}
              {isDownloadingImage ? 'Generating...' : 'Download Image'}
            </Button>
            <Button onClick={exportToCSV} variant="outline" size="sm">
              <FileText className="mr-2 h-4 w-4" />
              Export to CSV
            </Button>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};
